/**
 * Global "Find any case" search (#434).
 *
 * Mounted in the sticky top strip (router.tsx), so it's reachable from every
 * screen — the fix for a matter going un-findable once it leaves the book you
 * were watching. Search on Enter (one server query, no per-keystroke debounce);
 * each result shows the matter's status — the "which book is it in" answer —
 * and clicking opens the existing CaseDetailPanel over the current page from
 * this single shell-level mount.
 */

import { useState } from "react";
import { Search, X } from "lucide-react";
import { apiGet } from "@/lib/api";
import type { CaseLookupResponse, SearchResponse, SearchResult } from "@command-center/shared";
import { caseLookupToDetail, type CaseDetail } from "@/lib/case-detail";
import { CaseDetailPanel } from "@/components/case-detail-panel";

export function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [detail, setDetail] = useState<CaseDetail | null>(null);

  async function runSearch() {
    const q = query.trim();
    if (q === "") {
      setResults(null);
      return;
    }
    setSearching(true);
    try {
      const resp = await apiGet<SearchResponse>(
        `/api/search?q=${encodeURIComponent(q)}`,
      );
      setResults(resp.results);
    } catch {
      // A failed search just shows nothing — the box stays usable.
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function openCase(r: SearchResult) {
    setResults(null);
    setQuery("");
    try {
      const resp = await apiGet<CaseLookupResponse>(
        `/api/case/${encodeURIComponent(r.id)}`,
      );
      setDetail(caseLookupToDetail(resp));
    } catch {
      // Best-effort: a failed lookup just doesn't open the panel.
    }
  }

  return (
    <div className="border-b bg-background px-4 py-2.5">
      <div className="relative mx-auto max-w-2xl">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void runSearch();
          }}
          placeholder="Find any case…"
          aria-label="Find any case"
          data-testid="global-search-input"
          className="w-full rounded-lg border bg-card py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-active-edge focus:shadow-halo focus:outline-none"
        />
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setResults(null);
            }}
            aria-label="Clear search"
            data-testid="global-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}

        {results !== null ? (
          <div
            data-testid="global-search-results"
            className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border bg-card text-card-foreground shadow-lg"
          >
            {results.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                {searching ? "Searching…" : "No matches"}
              </div>
            ) : (
              results.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => void openCase(r)}
                  data-testid={`global-search-result-${r.id}`}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-accent/50"
                >
                  <span className="truncate font-medium">{r.name}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                    {r.originatingFirmName ? (
                      <span className="truncate">{r.originatingFirmName}</span>
                    ) : null}
                    {r.status ? (
                      <span className="rounded bg-muted px-1.5 py-0.5">
                        {r.status}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>

      {detail ? (
        <CaseDetailPanel detail={detail} onClose={() => setDetail(null)} />
      ) : null}
    </div>
  );
}
